import { Button, Divider, Drawer, Flex } from '@mantine/core';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { FundWithDealRes } from '@dealstack/shared/lib/generated';
import { CreateFundForm } from './CreateFundForm';
import { useFundCreateController } from './useFundCreateController';
import { DealRestrictionToggle } from '@dealstack/frontend/shared/components/DealRestrictionToggle/DealRestrictionToggle';
import { useEffect } from 'react';
import { CREATE_FUND_FORM } from '@dealstack/frontend/app/pages/Organization/OrganizationFunds/components/CreateFund/types.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	fund?: FundWithDealRes;
	organizationId: string;
}

export const CreateFundDrawer = ({ opened, onClose, onSuccess, fund, organizationId }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const isEditing = !!fund;

	const createFundController = useFundCreateController({
		callback: () => {
			onClose();
			onSuccess?.();
		},
		fund,
		organizationId,
	});

	useEffect(() => {
		if (opened) {
			createFundController.createFundForm.reset({
				[CREATE_FUND_FORM.NAME]: fund?.deal?.name,
				[CREATE_FUND_FORM.TARGET_NAME]: fund?.deal?.targetName,
				[CREATE_FUND_FORM.CURRENCY]: fund?.deal?.currency ?? undefined,
			});
		}
	}, [opened]);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				isEditing
					? tAdminDashboard('create_fund.drawer.edit_title')
					: tAdminDashboard('create_fund.drawer.title')
			}
		>
			<Flex direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap="1rem">
					<CreateFundForm createFundController={createFundController} hideSubmitButton />
					{isEditing && fund?.deal?.id ? (
						<>
							<Divider />
							<DealRestrictionToggle
								dealId={fund.deal.id}
								isRestricted={fund.isRestricted ?? false}
								onUpdated={onSuccess}
								withBorder={false}
							/>
						</>
					) : null}
				</Flex>
				<Button
					onClick={createFundController.createFundForm.handleSubmit(
						createFundController.onSubmit
					)}
					disabled={
						isEditing
							? createFundController.updateFundRequest.isPending
							: createFundController.createFundRequest.isPending
					}
				>
					{isEditing
						? tAdminDashboard('create_fund.save_button')
						: tAdminDashboard('create_fund.create_button')}
				</Button>
			</Flex>
		</Drawer>
	);
};
